import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, Stack,
  Switch, TextField, Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import ScienceIcon from '@mui/icons-material/Science';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { regOps } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';
import type { MedicalJurisdiction } from '@/types/medical';

type TestCase = {
  id: number;
  jurisdiction: number;
  release: number | null;
  name: string;
  scenario: Record<string, unknown>;
  expected_effect: 'block' | 'warn' | 'note' | 'allow';
  expected_reason_contains: string;
  is_mandatory: boolean;
  last_result: 'pass' | 'fail' | 'error' | 'pending' | null;
  last_run_at: string | null;
};

const EXPECTED_EFFECTS = [
  { code: 'allow', label: 'Allow' },
  { code: 'note',  label: 'Note' },
  { code: 'warn',  label: 'Warn' },
  { code: 'block', label: 'Block' },
];

export function RegOpsTestCasesPage() {
  const [params] = useSearchParams();
  const filterJurisdictionId = params.get('jurisdiction');

  const testsQ = useApiQuery({
    queryKey: ['regops', 'test-cases'],
    queryFn:  regOps.testCases.list as () => Promise<TestCase[]>,
  });
  const jurisdictionsQ = useApiQuery({
    queryKey: ['regops', 'jurisdictions'],
    queryFn:  regOps.jurisdictions.list as () => Promise<MedicalJurisdiction[]>,
  });

  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const all = testsQ.data ?? [];
    if (!filterJurisdictionId) return all;
    return all.filter((t) => t.jurisdiction === parseInt(filterJurisdictionId, 10));
  }, [testsQ.data, filterJurisdictionId]);

  const jurisdictionMap = useMemo(() => {
    const m = new Map<number, MedicalJurisdiction>();
    (jurisdictionsQ.data ?? []).forEach((j) => m.set(j.id, j));
    return m;
  }, [jurisdictionsQ.data]);

  return (
    <>
      <PageHeader
        title="Regulation test cases"
        subtitle="Frozen scenarios + expected effects. Run from a release to confirm the engine still does the right thing."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New test case
          </Button>
        }
      />

      <DataState query={testsQ} empty={{ title: 'No test cases yet' }}>
        {() => filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No test cases for this filter.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {filtered.map((t) => (
              <Row key={t.id} testCase={t}
                   jurisdictionCode={jurisdictionMap.get(t.jurisdiction)?.code ?? '?'} />
            ))}
          </Stack>
        )}
      </DataState>

      {creating && (
        <TestCaseDialog
          jurisdictions={jurisdictionsQ.data ?? []}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}

function Row({ testCase, jurisdictionCode }: { testCase: TestCase; jurisdictionCode: string }) {
  const result = testCase.last_result;
  const resultMeta = result === 'pass'
    ? { icon: <CheckCircleIcon fontSize="small" />, color: 'success.main' as const, label: 'pass' }
    : result === 'fail'
    ? { icon: <CancelIcon fontSize="small" />, color: 'error.main' as const, label: 'fail' }
    : result === 'error'
    ? { icon: <CancelIcon fontSize="small" />, color: 'error.main' as const, label: 'error' }
    : { icon: null, color: 'text.disabled' as const, label: 'never run' };

  return (
    <Card sx={{ borderColor: result === 'fail' || result === 'error' ? 'error.main' : 'divider' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: resultMeta.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {resultMeta.icon ?? <ScienceIcon />}
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {testCase.name}
              </Typography>
              <Chip size="small" label={jurisdictionCode}
                    variant="outlined" className="tabular" />
              {testCase.is_mandatory && (
                <Chip size="small" label="mandatory" color="warning" variant="outlined" />
              )}
              <Chip size="small" label={`expects ${testCase.expected_effect}`}
                    variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              <Box component="span" sx={{ color: resultMeta.color, fontWeight: 600 }}>
                {resultMeta.label.toUpperCase()}
              </Box>
              {testCase.last_run_at &&
                <span className="tabular"> · {formatDateTime(testCase.last_run_at)}</span>}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function TestCaseDialog({
  jurisdictions, onClose,
}: { jurisdictions: MedicalJurisdiction[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [jurisdictionId, setJurisdictionId] = useState('');
  const [name, setName] = useState('');
  const [scenario, setScenario] = useState('{\n  "rule_type": "prescribing_dispense_allowed",\n  "input": {}\n}');
  const [expectedEffect, setExpectedEffect] = useState('block');
  const [expectedReason, setExpectedReason] = useState('');
  const [isMandatory, setIsMandatory] = useState(true);

  let scenarioValid = true;
  let parsedScenario: Record<string, unknown> = {};
  try { parsedScenario = JSON.parse(scenario); } catch { scenarioValid = false; }

  const createMut = useApiMutation({
    mutationFn: () => regOps.testCases.create({
      jurisdiction: parseInt(jurisdictionId, 10),
      name,
      scenario: parsedScenario,
      expected_effect: expectedEffect,
      expected_reason_contains: expectedReason,
      is_mandatory: isMandatory,
    }),
    onSuccess: () => {
      toast.success('Test case created.');
      qc.invalidateQueries({ queryKey: ['regops', 'test-cases'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = jurisdictionId && name.trim() && scenarioValid;

  return (
    <Dialog open onClose={() => !createMut.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>New test case</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select required label="Jurisdiction"
                       value={jurisdictionId}
                       onChange={(e) => setJurisdictionId(e.target.value)}>
              {jurisdictions.map((j) => (
                <MenuItem key={j.id} value={String(j.id)}>{j.code}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Name"
                       value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="Tramadol Rx requires special license — PL" />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={6} label="Scenario (JSON input)"
              error={!scenarioValid}
              helperText="JSON passed to the regulation engine. Must include rule_type and input."
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              sx={{ '& textarea': { fontFamily: 'ui-monospace, monospace', fontSize: '0.875rem' } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Expected effect"
                       value={expectedEffect}
                       onChange={(e) => setExpectedEffect(e.target.value)}>
              {EXPECTED_EFFECTS.map((e) => (
                <MenuItem key={e.code} value={e.code}>{e.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Expected reason contains"
                       value={expectedReason}
                       onChange={(e) => setExpectedReason(e.target.value)}
                       placeholder="controlled substance" />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={isMandatory}
                               onChange={(_, v) => setIsMandatory(v)} />}
              label="Mandatory (release cannot go live if this fails)" />
          </Grid>
          {!scenarioValid && (
            <Grid item xs={12}>
              <Alert severity="error" variant="outlined">
                Scenario must be valid JSON before saving.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || createMut.isPending}
                onClick={() => createMut.mutate()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
